import { supabase } from '../../supabase';
import type { Snap, PaginatedResponse } from '@/types/database.types';
import { getLikeStatus } from '../likes';

export async function getSnaps(
  page = 1,
  limit = 9,
  searchQuery?: string,
  username?: string,
  inviteOnly = false
): Promise<PaginatedResponse<Snap>> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: [], total: 0, page, limit };
    }

    // Get user profile to check age verification status
    const { data: profile } = await supabase
      .from('profiles')
      .select('age_verified')
      .eq('user_id', user.id)
      .single();

    const isAgeVerified = profile?.age_verified || false;

    // Get invited SNAP IDs
    const { data: invitedSnaps } = await supabase
      .from('snap_invites')
      .select('snap_id')
      .eq('invited_user_id', user.id)
      .eq('status', 'accepted');

    const invitedSnapIds = invitedSnaps?.map(i => i.snap_id) || [];

    let query = supabase
      .from('snaps')
      .select(`
        id,
        title,
        total_votes,
        view_count,
        likes_count,
        created_at,
        ends_at,
        is_system,
        cashsnap,
        creator_id,
        description,
        is_anonymous,
        is_private,
        card_image_url,
        creator:user_profiles!inner (
          id,
          username,
          avatar_url,
          created_at,
          followers_count
        ),
        options:snap_options!snap_options_snap_id_fkey (
          id,
          title,
          description,
          image_url,
          vote_count,
          created_at
        ),
        comments:comments (count),
        categories:snap_categories (
          category
        )
      `, { count: 'exact' });

    // Add case-insensitive search with wildcards
    if (searchQuery?.trim()) {
      query = query.ilike('title', `%${searchQuery.trim()}%`);
    }

    // Add case-insensitive username filter if provided
    if (username?.trim()) {
      query = query.ilike('creator.username', `%${username.trim()}%`);
    }

    // Filter by invited SNAPs if requested
    if (inviteOnly && user) {
      // Only show private SNAPs where the user is invited
      query = query.in(
        'id',
        invitedSnapIds
      ).eq('is_private', true);
    } else {
      // Apply accessible SNAPs filter
      const conditions = ['is_private.is.false', `creator_id.eq.${user.id}`];
      
      // Only add invited SNAPs condition if there are any
      if (invitedSnapIds.length > 0) {
        conditions.push(`id.in.(${invitedSnapIds.join(',')})`);
      }

      query = query.or(conditions.join(','));
    }

    // If user is not age verified, exclude CashSNAPs
    if (!isAgeVerified) {
      query = query.eq('cashsnap', false);
    }

    // Get paginated data
    const { data: snapsData, error: snapsError, count } = await query
      .order('created_at', { ascending: false })
      .range((page - 1) * limit, page * limit - 1);

    if (snapsError) {
      console.error('Error fetching snaps:', snapsError);
      return { data: [], total: 0, page, limit };
    }

    if (!snapsData) {
      return { data: [], total: 0, page, limit };
    }

    // Get user's votes for these snaps
    const { data: userVotes } = await supabase
      .from('votes')
      .select('snap_id, option_id')
      .eq('user_id', user.id)
      .in('snap_id', snapsData.map(snap => snap.id));

    // Create a map of snap ID to voted option ID
    const userVotesMap = new Map();
    userVotes?.forEach(vote => {
      userVotesMap.set(vote.snap_id, vote.option_id);
    });

    // Get user's likes if authenticated
    let userLikes: { snap_id: string }[] = [];
    if (user) {
      const { data: likes } = await supabase
        .from('snap_likes')
        .select('snap_id')
        .eq('user_id', user.id);
      userLikes = likes || [];
    }

    // Get follow status for creators
    const creatorIds = snapsData.map(snap => snap.creator_id).filter(Boolean);
    const { data: following } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', user.id)
      .in('following_id', creatorIds);

    const followingSet = new Set(following?.map(f => f.following_id) || []);

    // Get like counts for all snaps
    const likePromises = snapsData.map(snap => getLikeStatus(snap.id));
    const likeStatuses = await Promise.all(likePromises);
    
    // Create a map of snap ID to like status
    const likeStatusMap = new Map(
      likeStatuses.map((status, index) => [snapsData[index].id, status])
    );

    // Get user marks for these snaps
    const { data: userMarks } = await supabase
      .from('snap_marks')
      .select('snap_id')
      .eq('user_id', user.id)
      .in('snap_id', snapsData.map(snap => snap.id));

    // Create a set of marked snap IDs
    const markedSnapsSet = new Set(userMarks?.map(mark => mark.snap_id) || []);

    const formattedData = snapsData.map(snap => {
      const likeStatus = likeStatusMap.get(snap.id) || { liked: false, count: 0 };
      const userVote = userVotesMap.get(snap.id) || null;
      
      return {
        ...snap,
        categories: snap.categories?.map((c: any) => c.category) || [],
        isOwner: user?.id === snap.creator_id,
        commentCount: snap.comments?.[0]?.count || 0,
        userLiked: likeStatus.liked,
        likes_count: likeStatus.count,
        userVote: userVote,
        isMarked: markedSnapsSet.has(snap.id),
        creator: {
          ...snap.creator,
          is_following: followingSet.has(snap.creator_id)
        }
      };
    });

    return {
      data: formattedData,
      total: count || 0,
      page,
      limit
    };
  } catch (error) {
    console.error('Error in getSnaps:', error);
    return { data: [], total: 0, page, limit };
  }
}

export async function getSnapById(id: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  // Get user profile to check age verification status
  const { data: profile } = await supabase
    .from('profiles')
    .select('age_verified')
    .eq('user_id', user.id)
    .single();

  const isAgeVerified = profile?.age_verified || false;
  
  // First check if SNAP exists and user has access
  const { data: snap, error: snapError } = await supabase
    .from('snaps')
    .select(`
      *,
      creator:user_profiles!inner(
        id,
        username,
        avatar_url,
        created_at
      ),
      options:snap_options!snap_options_snap_id_fkey(*),
      categories:snap_categories(category)
    `)
    .eq('id', id)
    .maybeSingle();

  if (snapError || !snap) {
    console.error('Error fetching SNAP:', snapError);
    throw new Error('SNAP not found');
  }

  // Check if this is a CashSNAP and user is not age verified
  if (snap.cashsnap && !isAgeVerified) {
    throw new Error('Age verification required to view CashSNAPs');
  }

  // Check if user has access to private SNAP
  if (snap.is_private) {
    const { data: invite } = await supabase
      .from('snap_invites')
      .select('status')
      .eq('snap_id', id)
      .eq('invited_user_id', user.id)
      .eq('status', 'accepted')
      .maybeSingle();

    const hasAccess = snap.creator_id === user.id || invite !== null;
    if (!hasAccess) {
      throw new Error('You do not have access to this private SNAP');
    }
  }

  // Get follow status for creator
  const { data: following } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', user.id)
    .eq('following_id', snap.creator_id)
    .maybeSingle();

  const { data: comments = [], error: commentsError } = await supabase
    .from('comments')
    .select(`
      *,
      user:user_profiles!inner(
        id,
        username,
        avatar_url,
        created_at
      )
    `)
    .eq('snap_id', id)
    .order('created_at', { ascending: false });

  if (commentsError) {
    console.error('Error fetching comments:', commentsError);
    throw new Error('Failed to fetch comments');
  }

  // Get user vote and like status
  let userVote = null;
  const { data: voteData } = await supabase
    .from('votes')
    .select('option_id')
    .eq('snap_id', id)
    .eq('user_id', user.id)
    .maybeSingle();

  if (voteData) {
    userVote = voteData.option_id;
  }

  // Get like status using the centralized function
  const likeStatus = await getLikeStatus(id);

  return {
    ...snap,
    categories: snap.categories?.map(c => c.category) || [],
    comments,
    isOwner: user?.id === snap.creator_id,
    userVote,
    userLiked: likeStatus.liked,
    likes_count: likeStatus.count,
    commentCount: comments.length,
    creator: {
      ...snap.creator,
      is_following: !!following
    }
  };
}

export async function getSnapsByUser(userId: string, page = 1, limit = 10): Promise<PaginatedResponse<Snap>> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: [], total: 0, page, limit };
    }

    // Get user profile to check age verification status
    const { data: profile } = await supabase
      .from('profiles')
      .select('age_verified')
      .eq('user_id', user.id)
      .single();

    const isAgeVerified = profile?.age_verified || false;

    // Get count of user's snaps
    const { count } = await supabase
      .from('snaps')
      .select('*', { count: 'exact', head: true })
      .eq('creator_id', userId)
      .eq('is_private', false); // Only count public snaps

    // Get paginated snaps
    let query = supabase
      .from('snaps')
      .select(`
        id,
        title,
        total_votes,
        view_count,
        likes_count,
        created_at,
        ends_at,
        is_system,
        cashsnap,
        creator_id,
        description,
        is_anonymous,
        is_private,
        card_image_url,
        creator:user_profiles!inner (
          id,
          username,
          avatar_url,
          created_at,
          followers_count
        ),
        options:snap_options!snap_options_snap_id_fkey (
          id,
          title,
          description,
          image_url,
          vote_count,
          created_at
        ),
        comments:comments (count),
        categories:snap_categories (
          category
        )
      `)
      .eq('creator_id', userId)
      .eq('is_private', false); // Only show public snaps

    // If user is not age verified, exclude CashSNAPs
    if (!isAgeVerified) {
      query = query.eq('cashsnap', false);
    }

    const { data: snapsData, error: snapsError } = await query
      .order('created_at', { ascending: false })
      .range((page - 1) * limit, page * limit - 1);

    if (snapsError) {
      console.error('Error fetching user snaps:', snapsError);
      return { data: [], total: 0, page, limit };
    }

    if (!snapsData) {
      return { data: [], total: 0, page, limit };
    }

    // Get user's votes for these snaps
    const { data: userVotes } = await supabase
      .from('votes')
      .select('snap_id, option_id')
      .eq('user_id', user.id)
      .in('snap_id', snapsData.map(snap => snap.id));

    // Create a map of snap ID to voted option ID
    const userVotesMap = new Map();
    userVotes?.forEach(vote => {
      userVotesMap.set(vote.snap_id, vote.option_id);
    });

    // Get user's likes
    const { data: userLikes } = await supabase
      .from('snap_likes')
      .select('snap_id')
      .eq('user_id', user.id);

    // Create a set of liked snap IDs
    const userLikesSet = new Set(userLikes?.map(like => like.snap_id) || []);

    // Get follow status for creator
    const { data: following } = await supabase
      .from('follows')
      .select('id')
      .eq('follower_id', user.id)
      .eq('following_id', userId)
      .maybeSingle();

    // Get user marks for these snaps
    const { data: userMarks } = await supabase
      .from('snap_marks')
      .select('snap_id')
      .eq('user_id', user.id)
      .in('snap_id', snapsData.map(snap => snap.id));

    // Create a set of marked snap IDs
    const markedSnapsSet = new Set(userMarks?.map(mark => mark.snap_id) || []);

    const formattedData = snapsData.map(snap => {
      return {
        ...snap,
        categories: snap.categories?.map((c: any) => c.category) || [],
        isOwner: user?.id === snap.creator_id,
        commentCount: snap.comments?.[0]?.count || 0,
        userLiked: userLikesSet.has(snap.id),
        userVote: userVotesMap.get(snap.id) || null,
        isMarked: markedSnapsSet.has(snap.id),
        creator: {
          ...snap.creator,
          is_following: !!following
        }
      };
    });

    return {
      data: formattedData,
      total: count || 0,
      page,
      limit
    };
  } catch (error) {
    console.error('Error in getSnapsByUser:', error);
    return { data: [], total: 0, page, limit };
  }
}